import { getInteractionIds } from '@mgo/core';
import Cookies from 'js-cookie';
import pageView from '../../mixins/pageView';
import { isDesktop } from '../../utils/deviceType';

export default {
  mixins: [pageView],
  computed: {
    showSimplifyExperience: () => JSON.parse(sessionStorage.getItem('showSimplifyExperience') ?? false),
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    progressStep() {
      return this.current / this.totalSteps;
    },
    totalSteps() {
      return isDesktop() ? 7 : 8;
    },
    current() {
      return isDesktop() ? 6 : 7;
    },
    offers() {
      return this.getDigitalOffers(this.rawOffers);
    },
    rawOffers() {
      return this.$store.get('instantSavings/offers');
    },
  },
  async created() {
    if (!this.$store.get('instantSavings/offers')?.length) {
      this.$store.set('instantSavings/token', this.$coa.token);
      this.$store.set('instantSavings/orderUuid', this.$route.params.orderUuid);
      await this.$store.dispatch('instantSavings/getOffers', {
        schemas: [
          'MGO Advantage Single',
          'MGO Advantage Double',
        ],
      });

      if (!this.$store.get('instantSavings/offers')?.length) {
        this.$router.replace({ name: 'confirm-order' });
      }
    }
  },
  async mounted() {
    // cfpsId and dcoa grabbed from query params
    if (!this.cfpsId && !this.dcoa) {
      this.$router.replace({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
    }
    this.$coa.coaUUID = this.$route.params.orderUuid;
    this.$store.commit('DISABLE_LOADER');
    this.impressOffers();
    this.prefetchConfirmationEmailAd();
    this.email = this.$store.get('instantSavings/email');
    await this.pageView();
    this.resetSession();
  },
  data() {
    return {
      cfpsId: this.$route?.query?.cfpsId || '',
      dcoa: this.$route?.query?.dcoa || false,
      email: '',
      offersOptIn: '',
      offersOptInError: false,
      selectedOffers: [],
      timer: null,
    };
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  },
  methods: {
    /**
     * Get Ampersend component to use based on template schema
     * @param {String} schemaName - name of the schema for the template data
     */
    getAmpComponent(schemaName) {
      const schemaMap = {
        'MGO Advantage Single': 'AdvantageSingle',
        'MGO Advantage Double': 'AdvantageDouble',
      };
      return schemaMap[schemaName];
    },
    isDigitalTemplateAvailable(offer) {
      return offer.templates.find(({ schema }) => this.schemaNames.indexOf(schema.name) > -1);
    },
    /**
     * Each offer has an array of template options
     * 'Double' should be prioritized over 'Single' if available
     * Each offer should end up with one template to render
     * @param {Array} offers
     * @return {Array}
     */
    getDigitalOffers(offers) {
      return offers
        .filter(this.isDigitalTemplateAvailable)
        // flatten templates to single template for each offer
        .map((offer) => {
          const { templates, id, ...rest } = offer;
          const [template] = templates
            .filter((t) => this.schemaNames.indexOf(t.schema.name) > -1)
            .sort((a, b) => (a.schema.name >= b.schema.name ? 1 : -1));

          return {
            adId: id,
            id,
            templateId: template.id,
            template,
            ...rest,
          };
        })
        // move double wide ads to front
        .sort((a, b) => (a.template.schema.name >= b.template.schema.name ? 1 : -1));
    },
    isSingleTemplate(schemaName) {
      return schemaName === 'MGO Advantage Single';
    },
    isDoubleTemplate(schemaName) {
      return schemaName === 'MGO Advantage Double';
    },
    isInViewport(element) {
      const { top, bottom } = element.getBoundingClientRect();
      const vHeight = window.innerHeight || document.documentElement.clientHeight;

      return (
        (top > 0 || bottom > 0)
          && top < vHeight
      );
    },
    toggleOfferSelect(id) {
      if (this.selectedOffers.indexOf(id) >= 0) {
        this.selectedOffers.splice(this.selectedOffers.indexOf(id), 1);
      } else {
        this.selectedOffers.push(id);
      }
    },
    impressOffers() {
      // Server events
      const impressions = getInteractionIds(this.offers);
      this.$store.dispatch('instantSavings/impress', impressions);
      // Web events
      impressions.forEach(({
        adId, adGroupId, accountId, templateId,
      }) => this.analytics.view.offerImpressed({
        adId, adGroupId, accountId, templateId, source: this.mgoAppName,

      }));
    },
    prefetchConfirmationEmailAd() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      // Give impressions time to flow through Ampersend to audience
      this.timer = setTimeout(() => {
        this.$store.dispatch('instantSavings/prefetch');
      }, 500);
    },
    registerSelections() {
      const selectedOffers = getInteractionIds(this.selectedOffers);
      this.$store.dispatch('instantSavings/select', selectedOffers);
      selectedOffers.forEach((offers) => {
        this.analytics.click.selectedOffers(offers);
      });
    },
    selectOffersOptIn(value) {
      this.offersOptIn = value;
      this.offersOptInError = false;
      this.$store.set('instantSavings/offersOptIn', value);
      this.analytics.click.offersOptIn(value, this.$route?.params?.orderUuid);
    },
    async scrollToFirstError() {
      const errorHTML = document.querySelector('.errors');
      if (errorHTML !== null) {
        setTimeout(() => {
          errorHTML.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    },
    resetSession() {
      if (this.cfpsId !== '') {
        this.$coa.reset();
        this.$deleteSession();
        Cookies.remove('coa');
        Cookies.remove('coa_complete');
        localStorage.removeItem('ampersend_data');
      }
    },
    async submit() {
      if (!this.offersOptIn) {
        this.offersOptInError = true;
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      this.registerSelections();

      this.$store.set('instantSavings/offers', []);
      this.next();
    },
    next() {
      const { utm_medium: medium } = this.$route.query;
      const selectedIds = this.selectedOffers.map(({ id }) => id);
      const selectedAll = this.selectedOffers.length === this.offers.length;

      this.$coa.trackContinueToConfirm('EMAIL', selectedIds, selectedAll);

      this.$router.push({
        name: 'confirm-order',
        params: { orderUuid: this.$route?.params?.orderUuid },
        query: {
          cfpsId: this.cfpsId,
          dcoa: this.dcoa,
          utm_medium: medium || 'sp',
        },
      });
    },
  },
};
